/* eslint-disable no-param-reassign */
/* eslint-disable no-return-assign */
import {
	createContext,
	FC,
	ReactNode,
	useContext,
	useEffect,
	useState,
	useRef,
	useCallback,
	useMemo,
} from 'react';
import { customBugsnagLogger } from '@/utils/bugsnag';
import { useDevicesContext } from './useDevices';

type Props = {
	userId: string;
	children: ReactNode;
};

type ContextType = {
	setDevice: (deviceType: string, deviceId: string, saveLocal: boolean) => void;
	mic?: MediaDeviceInfo;
	camera?: MediaDeviceInfo;
	stream?: MediaStream;
	getStreams: (stopStream?: boolean, stopAudio?: boolean) => void;
	deviceStatus: {
		video: boolean;
		audio: boolean;
	};
};

const Context = createContext<ContextType>(undefined as unknown as ContextType);

const DevicesTempContext: FC<Props> = ({ children, userId }: Props) => {
	const {
		camera: mainCamera,
		mic: mainMic,
		availableDevices,
	} = useDevicesContext();
	const [camera, setCamera] = useState<MediaDeviceInfo>();
	const [mic, setMic] = useState<MediaDeviceInfo>();
	const [stream, setStream] = useState<MediaStream>();
	const [deviceStatus, setDeviceStatus] = useState<{
		video: boolean;
		audio: boolean;
	}>({
		video: true,
		audio: true,
	});
	const isMountedRef = useRef(false);

	useEffect(() => {
		if (isMountedRef.current || !userId) return;
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, [userId]);

	const getStreams = useCallback(
		async (stopStream = true, stopAudio = false) => {
			if (stream) return;
			customBugsnagLogger.debug('getting temp stream stream');
			console.log('getting temp stream stream');
			const cameraId = localStorage.getItem('cameraId') || mainCamera?.deviceId;
			const micId = localStorage.getItem('micId') || mainMic?.deviceId;
			const constraints = cameraId
				? {
						video: {
							deviceId: { ideal: cameraId },
						},
						audio: {
							deviceId: { ideal: micId },
						},
				  }
				: { video: true, audio: true };

			await navigator?.mediaDevices
				.getUserMedia(constraints)
				.then(async (result) => {
					if (stopAudio) {
						result.getAudioTracks().forEach((track) => track.stop());
					}
					setStream(result);

					if (stopStream) {
						console.log('stoping stream');
						result.getTracks().forEach((track) => track.stop());
					}
				}).catch((error) => {
					console.log(error);
					switch (error.message) {
						case 'Permission denied':
						case 'Permission dismissed':
						case 'Permission denied by system':
							customBugsnagLogger.warn(error);
							break;
						default:
							customBugsnagLogger.error(error);
					}
				});
		},
		[mainCamera?.deviceId, mainMic?.deviceId, stream]
	);

	const stopStream = useCallback(() => {
		if (stream) {
			stream.getTracks().forEach((track) => {
				track.enabled = false;
				track.stop();
			});
			setStream(undefined);
		}
	}, [stream]);

	const stopStreamByType = useCallback(
		(streamType: 'audio' | 'video') => {
			if (stream) {
				stream
					.getTracks()
					.filter((element) => element.kind === streamType)
					.forEach((track) => {
						track.enabled = false;
						track.stop();
					});
				setStream(undefined);
			}
		},
		[stream]
	);

	useEffect(() => {
		window.addEventListener('beforeunload', stopStream);
		return () => {
			window.removeEventListener('beforeunload', stopStream);
			if (!isMountedRef.current) {
				stopStream();
			}
		};
	}, [stopStream]);

	useEffect(() => {
		setCamera(mainCamera);
	}, [mainCamera]);

	useEffect(() => {
		setMic(mainMic);
	}, [mainMic]);

	const values = useMemo(() => {
		const setDevice = (
			deviceType: string,
			deviceId: string,
			saveLocal: boolean
		) => {
			const newPreferred = availableDevices.find(
				(device) => device.deviceId === deviceId
			);
			if (newPreferred) {
				if (deviceType === 'video') {
					stopStreamByType('video');
					setCamera(newPreferred);
					if (saveLocal) {
						localStorage.setItem('cameraId', newPreferred?.deviceId);
					}
					getStreams(true);
				} else {
					stopStreamByType('audio');
					setMic(newPreferred);
					if (saveLocal) {
						localStorage.setItem('micId', newPreferred?.deviceId);
					}
					getStreams(true);
				}
			}
		};

		return {
			camera,
			mic,
			setDevice,
			stream,
			deviceStatus,
			getStreams,
		};
	}, [
		camera,
		mic,
		stream,
		deviceStatus,
		getStreams,
		availableDevices,
		stopStreamByType,
	]);

	return <Context.Provider value={values}>{children}</Context.Provider>;
};

export const useTempDevicesContext = (): ContextType => useContext(Context);

export default DevicesTempContext;
